import React, { useState } from 'react';
import { 
  Calendar, 
  Clock, 
  MapPin, 
  User, 
  Mail, 
  Phone, 
  CheckCircle, 
  DollarSign, 
  Shield, 
  Award, 
  FileText, 
  ChevronDown, 
  ChevronUp, 
  X,
  AlertCircle
} from 'lucide-react';

const TaxReturnSeminar: React.FC = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    phone: '',
    age: '',
    retirementSavings: '',
    howHeard: '',
    agreeToTerms: false
  });
  
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitted, setSubmitted] = useState(false);
  const [expandedFaq, setExpandedFaq] = useState<number | null>(null);

  // Seminar details - these could be props in a real implementation
  const seminarDetails = {
    title: "Free Retirement Income & Tax Efficiency Seminar",
    subtitle: "Plus: Qualify for Professional Tax Return Preparation at NO Additional Cost",
    date: "Tuesday, July 16, 2025",
    time: "6:30 PM - 8:00 PM",
    location: "Grand Hyatt Conference Center",
    address: "123 Financial Blvd, Suite 400, Anytown, USA",
    speaker: {
      name: "Michael Anderson, CPA, CFP®",
      title: "Senior Tax Strategist",
      experience: "25+ years of experience",
      credentials: ["Certified Public Accountant", "Certified Financial Planner™"]
    },
    seatsRemaining: 12
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
    
    // Clear error when field is changed
    if (errors[name]) {
      setErrors(prev => {
        const newErrors = {...prev};
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.fullName.trim()) {
      newErrors.fullName = 'Full name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email address is required';
    } else if (!/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i.test(formData.email)) {
      newErrors.email = 'Invalid email address';
    }
    
    if (!formData.phone.trim()) {
      newErrors.phone = 'Phone number is required';
    } else if (!/^\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/.test(formData.phone)) {
      newErrors.phone = 'Invalid phone number format';
    }
    
    if (!formData.age.trim()) {
      newErrors.age = 'Age is required';
    } else if (parseInt(formData.age) < 50) {
      newErrors.age = 'You must be 50 or older to qualify';
    }
    
    if (!formData.retirementSavings.trim()) {
      newErrors.retirementSavings = 'Please select your retirement savings range';
    }
    
    if (!formData.agreeToTerms) {
      newErrors.agreeToTerms = 'You must agree to the terms and conditions';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (validateForm()) {
      // In a real application, you would submit the form data to your backend
      console.log('Form submitted:', formData);
      setSubmitted(true);
    }
  };

  const toggleFaq = (index: number) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  const faqs = [
    {
      question: "What are the qualifications for the tax return at no additional cost?",
      answer: "To qualify for the professional tax return at no additional cost, you must be 50 years or older and have at least $100,000 in retirement savings or investments. You must also attend the entire seminar. This offer is for a standard 1040 tax return with Schedule A and B. Additional forms or complex returns may incur extra charges."
    },
    {
      question: "What topics will be covered in the seminar?",
      answer: "The seminar covers retirement income strategies, tax efficiency in retirement, Social Security optimization, protecting assets from market volatility, healthcare planning, and legacy planning. We'll also discuss specific tax-saving strategies for retirees and pre-retirees."
    },
    {
      question: "Do I need to bring anything to the seminar?",
      answer: "No, you don't need to bring anything. We'll provide all materials, including a comprehensive workbook, refreshments, and a pen for taking notes. Just bring yourself and any questions you have about retirement planning and tax efficiency."
    },
    {
      question: "Is there a cost to attend the seminar?",
      answer: "No, the seminar is completely free to attend. There is no cost and no obligation. We offer these educational seminars as a service to the community and as a way to introduce our firm to potential clients."
    },
    {
      question: "Can I bring my spouse or a guest?",
      answer: "Yes, we encourage you to bring your spouse or a partner who is involved in your financial decision-making. Please indicate this when you register so we can reserve enough seats and materials."
    },
    {
      question: "What if I need to cancel my registration?",
      answer: "We understand that plans can change. If you need to cancel your registration, please call us at least 24 hours in advance so we can offer your seat to someone on our waiting list. You can reach our event coordinator at [Phone Number]."
    }
  ];

  const benefits = [
    {
      icon: <DollarSign className="h-10 w-10 text-primary" />,
      title: "Tax-Efficient Retirement Strategies",
      description: "Learn how to minimize taxes on your retirement income, including Social Security benefits, IRA/401(k) withdrawals, and investment income."
    },
    {
      icon: <Shield className="h-10 w-10 text-primary" />,
      title: "Protect Your Retirement Savings",
      description: "Discover strategies to shield your hard-earned savings from market volatility, inflation, and unexpected healthcare costs."
    },
    {
      icon: <Award className="h-10 w-10 text-primary" />,
      title: "Maximize Social Security Benefits",
      description: "Understand the optimal timing and strategies to maximize your Social Security benefits based on your unique situation."
    },
    {
      icon: <FileText className="h-10 w-10 text-primary" />,
      title: "Professional Tax Return at NO Additional Cost",
      description: "Qualified attendees receive professional tax return preparation for the upcoming tax season at no additional cost ($350 value)."
    }
  ];

  return (
    <div className="min-h-screen bg-gray-50">
      {!submitted ? (
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-24">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left Column - Content */}
            <div>
              <div className="inline-flex items-center bg-primary/10 rounded-full px-4 py-2 text-primary mb-6">
                <Calendar className="h-5 w-5 mr-2" />
                {seminarDetails.date} | Limited Seating
              </div>
              
              <h1 className="text-4xl md:text-5xl font-bold text-gray-900 mb-6 leading-tight">
                {seminarDetails.title}
                <span className="block text-primary mt-2">{seminarDetails.subtitle}</span>
              </h1>
              
              <p className="text-xl text-gray-600 mb-8">
                If you're 50 or older with $100,000+ in retirement savings, join us to discover powerful strategies to maximize your retirement income and minimize taxes.
              </p>
              
              <div className="bg-white rounded-lg shadow-sm p-6 mb-8 border border-gray-200">
                <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-4">
                  <div className="flex items-start">
                    <Calendar className="h-6 w-6 text-primary mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Date & Time</p>
                      <p className="text-gray-600">{seminarDetails.date}</p>
                      <p className="text-gray-600">{seminarDetails.time}</p>
                    </div>
                  </div>
                  <div className="flex items-start">
                    <MapPin className="h-6 w-6 text-primary mr-3 flex-shrink-0" />
                    <div>
                      <p className="font-medium">Location</p>
                      <p className="text-gray-600">{seminarDetails.location}</p>
                      <p className="text-gray-600">{seminarDetails.address}</p>
                    </div>
                  </div>
                </div>
                <div className="flex items-start mt-4">
                  <User className="h-6 w-6 text-primary mr-3 flex-shrink-0" />
                  <div>
                    <p className="font-medium">Featured Speaker</p>
                    <p className="text-gray-800">{seminarDetails.speaker.name}</p>
                    <p className="text-gray-600">{seminarDetails.speaker.title}</p>
                    <p className="text-gray-600 text-sm">{seminarDetails.speaker.experience}</p>
                  </div>
                </div>
              </div>
              
              <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-8">
                <div className="flex">
                  <AlertCircle className="h-6 w-6 text-yellow-600 flex-shrink-0" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">Limited Availability</h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      <p>Only {seminarDetails.seatsRemaining} seats remaining for this exclusive event. Reserve your spot now to avoid disappointment.</p>
                    </div>
                  </div>
                </div>
              </div>
              
              <div className="space-y-6 mb-8">
                <h2 className="text-2xl font-bold text-gray-900">What You'll Learn:</h2>
                {benefits.map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <div className="flex-shrink-0 mt-1">
                      {benefit.icon}
                    </div>
                    <div className="ml-4">
                      <h3 className="text-lg font-semibold text-gray-900">{benefit.title}</h3>
                      <p className="text-gray-600">{benefit.description}</p>
                    </div>
                  </div>
                ))}
              </div>
              
              <div className="bg-primary/5 border border-primary/20 rounded-lg p-6">
                <h3 className="text-xl font-bold text-gray-900 mb-4">Who Should Attend:</h3>
                <ul className="space-y-3">
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <span>Adults age 50 and older</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <span>Individuals with $100,000+ in retirement savings</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <span>Those concerned about taxes in retirement</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <span>Anyone wanting to maximize retirement income</span>
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="h-5 w-5 text-primary mr-3 mt-0.5" />
                    <span>People seeking protection from market volatility</span>
                  </li>
                </ul>
              </div>
            </div>
            
            {/* Right Column - Registration Form */}
            <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-200">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">
                Reserve Your Seat
              </h2>
              
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="fullName" className="block text-sm font-medium text-gray-700 mb-1">
                    Full Name <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <User className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="text"
                      id="fullName"
                      name="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.fullName ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="John Smith"
                    />
                  </div>
                  {errors.fullName && (
                    <p className="mt-2 text-sm text-red-600">{errors.fullName}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Mail className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.email ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="you@example.com"
                    />
                  </div>
                  {errors.email && (
                    <p className="mt-2 text-sm text-red-600">{errors.email}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-1">
                    Phone Number <span className="text-red-500">*</span>
                  </label>
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <Phone className="h-5 w-5 text-gray-400" />
                    </div>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      className={`pl-10 block w-full rounded-lg border ${errors.phone ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                      placeholder="(555) 123-4567"
                    />
                  </div>
                  {errors.phone && (
                    <p className="mt-2 text-sm text-red-600">{errors.phone}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="age" className="block text-sm font-medium text-gray-700 mb-1">
                    Your Age <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    id="age"
                    name="age"
                    min="18"
                    max="100"
                    value={formData.age}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border ${errors.age ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                    placeholder="Enter your age"
                  />
                  {errors.age && (
                    <p className="mt-2 text-sm text-red-600">{errors.age}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="retirementSavings" className="block text-sm font-medium text-gray-700 mb-1">
                    Retirement Savings <span className="text-red-500">*</span>
                  </label>
                  <select
                    id="retirementSavings"
                    name="retirementSavings"
                    value={formData.retirementSavings}
                    onChange={handleChange}
                    className={`block w-full rounded-lg border ${errors.retirementSavings ? 'border-red-300' : 'border-gray-300'} py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary`}
                  >
                    <option value="">Select your savings range</option>
                    <option value="under100k">Under $100,000</option>
                    <option value="100k-250k">$100,000 - $250,000</option>
                    <option value="250k-500k">$250,000 - $500,000</option>
                    <option value="500k-1m">$500,000 - $1,000,000</option>
                    <option value="over1m">Over $1,000,000</option>
                  </select>
                  {errors.retirementSavings && (
                    <p className="mt-2 text-sm text-red-600">{errors.retirementSavings}</p>
                  )}
                </div>
                
                <div>
                  <label htmlFor="howHeard" className="block text-sm font-medium text-gray-700 mb-1">
                    How did you hear about us?
                  </label>
                  <select
                    id="howHeard"
                    name="howHeard"
                    value={formData.howHeard}
                    onChange={handleChange}
                    className="block w-full rounded-lg border border-gray-300 py-3 text-gray-900 shadow-sm focus:border-primary focus:ring-primary"
                  >
                    <option value="">Select an option</option>
                    <option value="radio">Radio</option>
                    <option value="tv">TV</option>
                    <option value="newspaper">Newspaper</option>
                    <option value="mail">Direct Mail</option>
                    <option value="email">Email</option>
                    <option value="friend">Friend/Family</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                
                <div className="space-y-4">
                  <div className="flex items-start">
                    <div className="flex items-center h-5">
                      <input
                        id="agreeToTerms"
                        name="agreeToTerms"
                        type="checkbox"
                        checked={formData.agreeToTerms}
                        onChange={handleChange}
                        className={`h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary ${errors.agreeToTerms ? 'border-red-300' : ''}`}
                      />
                    </div>
                    <div className="ml-3">
                      <label htmlFor="agreeToTerms" className="text-sm text-gray-700">
                        I agree to be contacted about this event and understand my information will be kept confidential in accordance with your <a href="#" className="text-primary hover:underline">Privacy Policy</a>.
                      </label>
                    </div>
                  </div>
                  {errors.agreeToTerms && (
                    <p className="mt-1 text-sm text-red-600">{errors.agreeToTerms}</p>
                  )}
                </div>
                
                <button
                  type="submit"
                  className="w-full bg-primary hover:bg-primary-dark text-white font-bold py-3 px-4 rounded-lg text-lg transition-colors duration-200 shadow-md hover:shadow-lg"
                >
                  Reserve My Seat Now
                </button>
                
                <p className="text-center text-gray-500 text-sm mt-4">
                  Only {seminarDetails.seatsRemaining} seats remaining for this exclusive event
                </p>
              </form>
            </div>
          </div>
          
          {/* Trust Indicators Section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Why Attend Our Seminar?</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                  <Award className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Expert Guidance</h3>
                <p className="text-gray-600">Learn from certified financial professionals with decades of experience in retirement planning and tax strategies.</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                  <FileText className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Actionable Strategies</h3>
                <p className="text-gray-600">Take home practical, actionable strategies you can implement immediately to improve your retirement outlook.</p>
              </div>
              
              <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200 text-center">
                <div className="inline-flex items-center justify-center h-16 w-16 rounded-full bg-primary/10 text-primary mb-4">
                  <DollarSign className="h-8 w-8" />
                </div>
                <h3 className="text-xl font-semibold mb-2">Exclusive Offer</h3>
                <p className="text-gray-600">Qualified attendees receive professional tax return preparation ($350 value) at no additional cost.</p>
              </div>
            </div>
          </div>
          
          {/* Testimonials */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">What Past Attendees Say</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  quote: "The strategies I learned helped me save over $4,200 in taxes last year. The tax return at no additional cost was an amazing bonus!",
                  author: "Robert J.",
                  location: "Retired Engineer"
                },
                {
                  quote: "Finally, retirement planning advice that makes sense! The presenters explained complex concepts in a way that was easy to understand.",
                  author: "Margaret S.",
                  location: "Retired Teacher"
                },
                {
                  quote: "I've attended many financial seminars, but this one provided the most practical, actionable advice by far. Highly recommended!",
                  author: "Thomas W.",
                  location: "Business Owner"
                }
              ].map((testimonial, index) => (
                <div key={index} className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
                  <p className="text-gray-600 mb-4 italic">"{testimonial.quote}"</p>
                  <div>
                    <p className="font-semibold">{testimonial.author}</p>
                    <p className="text-sm text-gray-500">{testimonial.location}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
          
          {/* FAQ Section */}
          <div className="mt-16">
            <h2 className="text-2xl font-bold text-center text-gray-900 mb-8">Frequently Asked Questions</h2>
            <div className="max-w-3xl mx-auto">
              {faqs.map((faq, index) => (
                <div key={index} className="mb-4">
                  <button
                    className="flex justify-between items-center w-full p-4 bg-white rounded-lg shadow-sm border border-gray-200 text-left"
                    onClick={() => toggleFaq(index)}
                  >
                    <span className="font-medium text-gray-900">{faq.question}</span>
                    {expandedFaq === index ? (
                      <ChevronUp className="h-5 w-5 text-gray-500" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-gray-500" />
                    )}
                  </button>
                  {expandedFaq === index && (
                    <div className="p-4 bg-gray-50 rounded-b-lg border-x border-b border-gray-200">
                      <p className="text-gray-600">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
          
          {/* Final CTA */}
          <div className="mt-16 text-center">
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Don't Miss This Opportunity</h2>
            <p className="text-xl text-gray-600 mb-8 max-w-3xl mx-auto">
              Learn valuable retirement strategies AND qualify for professional tax return preparation at no additional cost ($350 value).
            </p>
            <button
              onClick={() => {
                // Scroll to registration form
                document.querySelector('.bg-white.rounded-xl.shadow-lg')?.scrollIntoView({ behavior: 'smooth' });
              }}
              className="bg-primary hover:bg-primary-dark text-white font-bold py-3 px-8 rounded-lg text-lg transition-colors duration-200 shadow-md hover:shadow-lg"
            >
              Reserve Your Seat Now
            </button>
          </div>
        </div>
      ) : (
        <div className="max-w-3xl mx-auto px-4 py-16 text-center">
          <div className="bg-white rounded-xl shadow-xl p-8 border border-gray-200">
            <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-6" />
            <h2 className="text-3xl font-bold text-gray-900 mb-4">Registration Confirmed!</h2>
            <p className="text-xl text-gray-600 mb-6">
              Thank you for registering for our Free Retirement Income & Tax Efficiency Seminar.
            </p>
            <div className="bg-gray-50 rounded-lg p-6 mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Your Seminar Details:</h3>
              <div className="space-y-3">
                <div className="flex items-start justify-center">
                  <Calendar className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <span><strong>Date:</strong> {seminarDetails.date}</span>
                </div>
                <div className="flex items-start justify-center">
                  <Clock className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <span><strong>Time:</strong> {seminarDetails.time}</span>
                </div>
                <div className="flex items-start justify-center">
                  <MapPin className="h-5 w-5 text-primary mr-2 mt-0.5" />
                  <div>
                    <div><strong>Location:</strong> {seminarDetails.location}</div>
                    <div>{seminarDetails.address}</div>
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
              <div className="flex">
                <AlertCircle className="h-5 w-5 text-blue-500 mt-0.5 mr-2" />
                <div>
                  <h4 className="text-sm font-medium text-blue-800">What happens next?</h4>
                  <p className="mt-1 text-sm text-blue-700">
                    You'll receive a confirmation email with all the details. We'll also call you 24 hours before the event to confirm your attendance. If you have any questions, please call us at [Phone Number].
                  </p>
                </div>
              </div>
            </div>
            <div className="mt-6">
              <p className="font-medium text-gray-900 mb-2">Add to your calendar:</p>
              <div className="flex justify-center space-x-4">
                <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-800 transition-colors">
                  Google Calendar
                </button>
                <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-800 transition-colors">
                  Apple Calendar
                </button>
                <button className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-md text-gray-800 transition-colors">
                  Outlook
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Footer with Disclaimers */}
      <div className="bg-gray-100 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl mx-auto text-center">
            <h3 className="text-sm font-semibold text-gray-700 mb-2">Important Disclosures</h3>
            <p className="text-xs text-gray-600 mb-2">
              The tax return at no additional cost offer is valid for individuals age 50+ with at least $100,000 in retirement savings who attend the entire seminar. 
              This offer applies to a standard Form 1040 with Schedule A and B. Additional forms or complex returns may incur extra charges.
            </p>
            <p className="text-xs text-gray-600">
              [Company Name] is a registered investment advisor. Registration does not imply a certain level of skill or training nor does it imply endorsement by any regulatory authority.
              This seminar is for educational purposes only and is not intended to provide specific investment, tax, or legal advice.
              Please consult with qualified professionals for advice specific to your situation.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TaxReturnSeminar;